import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { InquiryForm } from "@/components/ur/InquiryForm";
import { KITCHEN_LAYOUTS, KITCHEN_MATERIALS, IMAGES, ALL_SERVICES, BRAND } from "@/data/urban";
import { FinalCTA } from "@/components/ur/FinalCTA";

const Page = ({ children }) => { useEffect(() => window.scrollTo({ top: 0 }), []); return children; };

const Hero = ({ overline, h1, sub, price, img, cta = "Get Quote", inquirySource, service, defaultBudget }) => (
  <section className="relative min-h-[70vh] flex items-center overflow-hidden pt-24 pb-14 bg-black text-white">
    <div className="absolute inset-0 opacity-40"><img src={img} alt={h1} className="h-full w-full object-cover" /></div>
    <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/50 to-black/10" />
    <div className="relative z-10 max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 grid lg:grid-cols-12 gap-10 items-center w-full">
      <div className="lg:col-span-7">
        <div className="overline text-white/70">{overline}</div>
        <h1 className="mt-3 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight">{h1}</h1>
        {price && <div className="mt-4 font-heading text-2xl font-extrabold text-white">{price}</div>}
        <p className="mt-4 text-white/80 max-w-xl leading-relaxed">{sub}</p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/contact" className="inline-flex items-center gap-2 bg-white text-black px-6 py-3 rounded-md font-bold uppercase tracking-wider text-sm hover:bg-white/90">{cta} <ArrowRight className="h-4 w-4" /></Link>
          <Link to="/designs" className="inline-flex items-center gap-2 border border-white/40 text-white px-6 py-3 rounded-md font-bold uppercase tracking-wider text-sm hover:bg-white hover:text-black">View Designs</Link>
        </div>
      </div>
      <div className="lg:col-span-5">
        <InquiryForm heading="Get a Free Quote" subheading="Free consultation · Reply within 24 hrs" source={inquirySource} defaultService={service} defaultBudget={defaultBudget} dark testId={`${inquirySource}-inquiry`} />
      </div>
    </div>
  </section>
);

export const ModularKitchen = () => (
  <Page>
    <Hero overline="Modular Kitchen in Gurgaon" h1="Modular Kitchen in Gurgaon" price="Starting from ₹1,200/sq.ft." sub="Functional, beautiful and customized kitchens designed for modern Indian homes. HDHMR, laminates, PU finishes and quality hardware — all within your budget." img={IMAGES.kitchen1} inquirySource="modular-kitchen-page" service="Modular Kitchen" defaultBudget="₹3L – ₹5L" />

    <section className="py-20 bg-canvas">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
        <div className="overline text-ink-soft">Kitchen Layouts</div>
        <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-extrabold text-ink">Pick the layout that fits your space.</h2>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {KITCHEN_LAYOUTS.map((l) => (
            <div key={l.key} className="rounded-2xl overflow-hidden bg-white border border-hairline">
              <div className="aspect-[4/3] overflow-hidden"><img src={l.img} alt={l.title} loading="lazy" className="h-full w-full object-cover" /></div>
              <div className="p-5"><h3 className="font-heading text-lg font-bold text-ink">{l.title}</h3><p className="mt-1.5 text-sm text-ink-soft">{l.desc}</p></div>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="py-16 bg-white">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
        <div className="overline text-ink-soft">Materials & Accessories</div>
        <h2 className="mt-3 font-heading text-2xl sm:text-3xl font-extrabold text-ink">Quality materials, honest pricing.</h2>
        <div className="mt-8 flex flex-wrap gap-2">
          {KITCHEN_MATERIALS.map((m) => <span key={m} className="px-4 py-2 rounded-md border border-hairline text-sm text-ink">{m}</span>)}
        </div>
        <p className="mt-6 text-xs text-ink-soft italic">Starting price is indicative and may vary according to layout, size, materials, finish, hardware and accessories.</p>
      </div>
    </section>

    <FinalCTA />
  </Page>
);

export const Wardrobes = () => (
  <Page>
    <Hero overline="Modular Wardrobes" h1="Modular Wardrobes in Gurgaon" sub="Sliding, hinged and walk-in wardrobes engineered for space and finish. Choose HDHMR or marine ply cores with laminate, membrane or acrylic shutters." img={IMAGES.wardrobe3} inquirySource="wardrobes-page" service="Wardrobes" defaultBudget="₹1L – ₹3L" />
    <section className="py-20 bg-canvas">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            { t: "Hinged Wardrobes", d: "Classic hinged doors with soft-close hinges and loft storage.", i: IMAGES.wardrobe4 },
            { t: "Sliding & Full-Height Wardrobes", d: "Space-saving sliding wardrobes and full-height storage.", i: IMAGES.wardrobe5 },
            { t: "Kids Wardrobes", d: "Playful, safe wardrobes designed for growing kids.", i: IMAGES.wardrobe6 },
            { t: "Premium High-Gloss", d: "Sleek acrylic shutters with slim brass hardware.", i: IMAGES.wardrobe7 },
            { t: "White Handleless", d: "Minimal handleless wardrobes with recessed profiles.", i: IMAGES.wardrobe3 },
            { t: "Walk-in Wardrobes", d: "Boutique-style with dresser, drawers & lighting.", i: IMAGES.wardrobe5 },
          ].map((c) => (
            <div key={c.t} className="rounded-2xl overflow-hidden bg-white border border-hairline">
              <div className="aspect-[4/3]"><img src={c.i} alt={c.t} loading="lazy" className="h-full w-full object-cover" /></div>
              <div className="p-5"><h3 className="font-heading font-bold text-ink">{c.t}</h3><p className="mt-1.5 text-sm text-ink-soft">{c.d}</p></div>
            </div>
          ))}
        </div>
      </div>
    </section>
    <FinalCTA />
  </Page>
);

export const Doors = () => (
  <Page>
    <Hero overline="Designer Doors" h1="Designer Doors in Gurgaon" sub="HDHMR, CNC and laminate doors. Solid, sound-proof and CNC-carved options for main doors, interior doors and door frames." img={IMAGES.door1} inquirySource="doors-page" service="Designer Doors" defaultBudget="₹1L – ₹3L" />
    <section className="py-20 bg-canvas">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 grid grid-cols-1 md:grid-cols-3 gap-5">
        {[
          { t: "CNC Sunburst Main Door", d: "Statement double main door with CNC-carved sunburst pattern in solid teak.", i: IMAGES.door1 },
          { t: "Walnut 6-Panel Interior", d: "6-panel walnut veneer door with premium lever handle.", i: IMAGES.door2 },
          { t: "Teak Door with Glass Strip", d: "Modern teak door with a tinted vertical glass strip.", i: IMAGES.door3 },
          { t: "Sliding Flush Door", d: "Space-saving sliding door with full-height brass handle.", i: IMAGES.door4 },
          { t: "Brass Inlay Interior Door", d: "Walnut door with horizontal brass inlays and antique lever.", i: IMAGES.door5 },
          { t: "HDHMR Main Doors", d: "Heavy solid main doors with premium hardware and finish options.", i: IMAGES.door1 },
        ].map((c) => (
          <div key={c.t} className="rounded-2xl overflow-hidden bg-white border border-hairline">
            <div className="aspect-[4/3]"><img src={c.i} alt={c.t} loading="lazy" className="h-full w-full object-cover" /></div>
            <div className="p-5"><h3 className="font-heading font-bold text-ink">{c.t}</h3><p className="mt-1.5 text-sm text-ink-soft">{c.d}</p></div>
          </div>
        ))}
      </div>
    </section>
    <FinalCTA />
  </Page>
);

export const HomeInteriors = () => (
  <Page>
    <Hero overline="Home Interior Design" h1="Home Interior Design in Gurgaon" sub="Complete interior solutions for your home — modular kitchen, wardrobes, TV unit, pooja unit, wall panelling, false ceiling, painting, electrical and plumbing." img={IMAGES.living} inquirySource="home-interior-page" service="Home Interior" cta="Plan My Home" defaultBudget="₹10L – ₹15L" />
    <section className="py-20 bg-canvas">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {["Living Room", "Bedroom", "Modular Kitchen", "Wardrobes", "TV Unit", "Pooja Unit", "Doors", "Wall Panelling", "False Ceiling", "Painting", "Electrical", "Plumbing"].map((s) => (
            <div key={s} className="rounded-lg border border-hairline p-4 bg-white text-ink text-sm font-semibold text-center">{s}</div>
          ))}
        </div>
      </div>
    </section>
    <FinalCTA />
  </Page>
);

export const HomeRenovation = () => (
  <Page>
    <Hero overline="Home Renovation" h1="Home Renovation Services in Gurgaon" sub="Practical renovation for existing homes — kitchen, bathroom, painting, waterproofing, flooring, wall panelling, false ceiling, electrical, plumbing, civil work and door replacement." img={IMAGES.bathroom1} inquirySource="home-renovation-page" service="Home Renovation" cta="Get Renovation Estimate" defaultBudget="₹5L – ₹10L" />
    <section className="py-20 bg-canvas">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {["Complete Renovation", "Kitchen Renovation", "Bathroom Renovation", "Painting", "Waterproofing", "Flooring", "Wall Panelling", "False Ceiling", "Electrical", "Plumbing", "Civil Work", "Door Renovation"].map((s) => (
            <div key={s} className="rounded-lg border border-hairline p-4 bg-white text-ink text-sm font-semibold text-center">{s}</div>
          ))}
        </div>
      </div>
    </section>

    <section className="py-20 bg-white border-t border-hairline">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
        <div className="max-w-2xl">
          <div className="overline text-ink-soft">Bathroom Renovations</div>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-extrabold text-ink leading-tight">Spa-grade bathrooms, turnkey.</h2>
          <p className="mt-3 text-sm text-ink-soft">Waterproofing, plumbing rework, tiling, CP fittings and false ceiling — delivered as one accountable package.</p>
          <Link to="/bathroom-renovation-gurgaon" className="mt-5 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-ink hover:opacity-70">Explore Bathroom Renovation <ArrowRight className="h-3 w-3" /></Link>
        </div>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            { t: "Warm Terracotta Spa", d: "Wood-slat wall, terrazzo floor, brass shower & copper vessel sink.", i: IMAGES.bathroom1 },
            { t: "Blush & Brass Luxury", d: "Beige marble, blush cabinet and brushed brass fittings.", i: IMAGES.bathroom2 },
            { t: "Patterned-Tile Bathroom", d: "Moroccan feature tile, terrazzo floor and walnut vanity.", i: IMAGES.bathroom3 },
            { t: "Grey Stone & Oak", d: "Grey stone tiles with oak storage and matte-black fixtures.", i: IMAGES.bathroom4 },
          ].map((c) => (
            <div key={c.t} className="rounded-2xl overflow-hidden bg-canvas border border-hairline hover:border-ink transition-colors">
              <div className="aspect-[4/5]"><img src={c.i} alt={c.t} loading="lazy" className="h-full w-full object-cover" /></div>
              <div className="p-5"><h3 className="font-heading font-bold text-ink">{c.t}</h3><p className="mt-1.5 text-sm text-ink-soft">{c.d}</p></div>
            </div>
          ))}
        </div>
      </div>
    </section>

    <FinalCTA />
  </Page>
);

export const ServicesPage = () => (
  <Page>
    <div className="pt-32 pb-8 bg-canvas">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
        <div className="overline text-ink-soft">Services</div>
        <h1 className="mt-3 font-heading text-4xl sm:text-5xl font-extrabold text-ink">Everything you need for your home</h1>
      </div>
    </div>
    <section className="py-16 bg-canvas">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {ALL_SERVICES.map((s) => { const Ic = s.icon; return (
          <div key={s.key} className="rounded-2xl border border-hairline bg-white p-5 hover:border-ink transition-colors">
            <span className="h-10 w-10 rounded-md bg-black text-white flex items-center justify-center"><Ic className="h-5 w-5" /></span>
            <div className="mt-4 font-heading font-bold text-ink">{s.title}</div>
            <Link to="/contact" className="mt-3 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-ink hover:opacity-70">Get Quote <ArrowRight className="h-3 w-3" /></Link>
          </div>
        ); })}
      </div>
    </section>
    <FinalCTA />
  </Page>
);

export const AboutPage = () => (
  <Page>
    <div className="pt-32 pb-8 bg-canvas">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
        <div className="overline text-ink-soft">About</div>
        <h1 className="mt-3 font-heading text-4xl sm:text-5xl font-extrabold text-ink">About Urban Renovations</h1>
        <p className="mt-4 text-ink-soft max-w-2xl">Urban Renovations is the customer-facing brand of <span className="text-ink font-semibold">Urban Painter</span>, a home interior and renovation business working since {BRAND.since}. We focus on modular kitchens, wardrobes, doors, complete home interiors, renovation, painting and turnkey execution — all designed to be budget-friendly and honest.</p>
      </div>
    </div>
    <section className="py-16 bg-canvas">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 grid lg:grid-cols-2 gap-10">
        <div className="rounded-2xl overflow-hidden"><img src={IMAGES.living} alt="Urban Renovations" className="w-full h-[520px] object-cover" /></div>
        <div>
          <h2 className="font-heading text-2xl font-extrabold text-ink">What we do</h2>
          <ul className="mt-5 space-y-2 text-ink">
            {["Interior solutions", "Renovation & civil work", "Modular kitchens", "Wardrobes", "Doors", "Painting", "Waterproofing", "Custom solutions", "Budget-friendly execution"].map((f) => (
              <li key={f} className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-black" />{f}</li>
            ))}
          </ul>
          <p className="mt-6 text-ink-soft text-sm">We do not claim awards, certifications or invented project statistics. What we do offer is a transparent process, quality materials and honest pricing.</p>
        </div>
      </div>
    </section>
    <FinalCTA />
  </Page>
);

export const ContactPage = () => (
  <Page>
    <div className="pt-32 pb-8 bg-canvas">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
        <div className="overline text-ink-soft">Contact</div>
        <h1 className="mt-3 font-heading text-4xl sm:text-5xl font-extrabold text-ink">Let's Build Your Home</h1>
        <p className="mt-4 text-ink-soft max-w-2xl">A service brand of <span className="text-ink font-semibold">Urban Painter</span> · Working Since {BRAND.since}</p>
      </div>
    </div>
    <section className="py-16 bg-canvas">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 grid lg:grid-cols-2 gap-10 items-start">
        <div>
          <div className="rounded-2xl border border-hairline bg-white p-6 space-y-4">
            <a href={`tel:${BRAND.phoneRaw}`} className="block"><div className="overline text-ink-soft">Phone</div><div className="font-heading text-2xl font-extrabold text-ink">{BRAND.phone}</div></a>
            <div><div className="overline text-ink-soft">Address</div><div className="text-ink text-sm mt-1">{BRAND.address}</div></div>
            <div><div className="overline text-ink-soft">Email</div><div className="text-ink text-sm mt-1">{BRAND.email}</div></div>
            <div><div className="overline text-ink-soft">Hours</div><div className="text-ink text-sm mt-1">Mon–Sat · 9:30 AM – 7:30 PM</div></div>
          </div>
          <div className="mt-6 rounded-2xl overflow-hidden border border-hairline">
            <iframe title="Urban Renovations location" src="https://www.google.com/maps?q=Kherki+Daula,+Gurugram,+Haryana+122002&output=embed" width="100%" height="360" style={{ border: 0 }} loading="lazy" />
          </div>
        </div>
        <InquiryForm heading="Get a Free Quote" subheading="Free consultation · Reply within 24 hrs" source="contact-page" testId="contact-inquiry" />
      </div>
    </section>
  </Page>
);

export const BlogPage = () => (
  <Page>
    <div className="pt-32 pb-8 bg-canvas">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
        <div className="overline text-ink-soft">Journal</div>
        <h1 className="mt-3 font-heading text-4xl sm:text-5xl font-extrabold text-ink">Interior & renovation ideas</h1>
      </div>
    </div>
    <div className="py-16 bg-canvas"><div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {require("@/data/urban").BLOG.map((p) => (
          <Link key={p.slug} to={`/blog/${p.slug}`} className="group rounded-2xl overflow-hidden bg-white border border-hairline hover:-translate-y-0.5 transition-transform">
            <div className="aspect-[16/10] overflow-hidden"><img src={p.img} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" /></div>
            <div className="p-5">
              <div className="text-xs text-ink-soft">{p.category} · {p.date}</div>
              <h3 className="mt-2 font-heading text-lg font-bold text-ink">{p.title}</h3>
              <p className="mt-2 text-sm text-ink-soft">{p.excerpt}</p>
            </div>
          </Link>
        ))}
      </div>
    </div></div>
    <FinalCTA />
  </Page>
);

export const BlogDetail = () => {
  const slug = window.location.pathname.split("/").pop();
  const BLOG = require("@/data/urban").BLOG;
  const post = BLOG.find((p) => p.slug === slug) || BLOG[0];
  useEffect(() => window.scrollTo({ top: 0 }), [slug]);
  return (
    <>
      <article className="pt-32 pb-16 bg-canvas">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="text-xs text-ink-soft">{post.category} · {post.date}</div>
          <h1 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-ink leading-tight">{post.title}</h1>
          <img src={post.img} alt={post.title} className="mt-8 w-full aspect-[16/9] object-cover rounded-2xl" />
          <div className="mt-8 prose prose-neutral max-w-none text-ink">
            <p className="text-ink-soft leading-relaxed">{post.excerpt}</p>
            <p className="text-ink-soft leading-relaxed">This is an editorial guide from Urban Renovations. Our team writes practical, budget-focused content to help homeowners make better decisions about kitchens, wardrobes, doors, painting and renovation.</p>
            <h2 className="font-heading text-2xl font-extrabold text-ink mt-8">Key takeaways</h2>
            <ul className="mt-3 space-y-1 text-ink-soft">
              <li>Set a realistic budget before finalising the layout.</li>
              <li>Choose materials based on usage — not just look.</li>
              <li>Always insist on an itemised quotation.</li>
            </ul>
          </div>
        </div>
      </article>
      <FinalCTA />
    </>
  );
};

export const FAQPage = () => {
  const FAQ = require("@/components/ur/FAQ").FAQ;
  return (
    <Page>
      <div className="pt-32 pb-8 bg-canvas">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="overline text-ink-soft">FAQ</div>
          <h1 className="mt-3 font-heading text-4xl sm:text-5xl font-extrabold text-ink">Questions, answered.</h1>
        </div>
      </div>
      <FAQ />
      <FinalCTA />
    </Page>
  );
};

export const Privacy = () => (
  <Page>
    <div className="pt-32 pb-24 bg-canvas">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-10 text-ink">
        <h1 className="font-heading text-4xl font-extrabold">Privacy Policy</h1>
        <p className="mt-6 text-ink-soft">Urban Renovations respects your privacy. This policy explains how we collect and use information from our website visitors and customers.</p>
        <h2 className="mt-8 font-heading text-xl font-bold">Information we collect</h2>
        <p className="mt-2 text-ink-soft">Name, phone, email and enquiry details voluntarily submitted through our enquiry forms and chatbot.</p>
        <h2 className="mt-8 font-heading text-xl font-bold">How we use it</h2>
        <p className="mt-2 text-ink-soft">Solely to respond to your enquiry, provide quotations, share design proposals and follow up. We do not sell or share your details with third parties.</p>
        <h2 className="mt-8 font-heading text-xl font-bold">Contact</h2>
        <p className="mt-2 text-ink-soft">For any privacy questions, write to us at {BRAND.email} or call {BRAND.phone}.</p>
      </div>
    </div>
  </Page>
);

export const Terms = () => (
  <Page>
    <div className="pt-32 pb-24 bg-canvas">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-10 text-ink">
        <h1 className="font-heading text-4xl font-extrabold">Terms & Conditions</h1>
        <p className="mt-6 text-ink-soft">All indicative prices on this website are starting prices and may vary based on layout, size, materials, finish, hardware and accessories. Final pricing is confirmed only after a site visit and detailed quotation. Design references are for inspiration only.</p>
      </div>
    </div>
  </Page>
);
